import { useMutation } from '@apollo/client';
import { newMessagePlaceholderId } from 'constants/messages';
import { ERRORS } from 'constants/notify';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import moment from 'moment';
import { useMemo } from 'react';
import { checkUserIsInChatRoom } from 'routes/Transaction/components/TransactionMessageTab/hooks';
import { SEND_MESSAGE } from 'services/graphql/mutations/message';
import { GET_CHATROOM, GET_MESSAGES } from 'services/graphql/queries/message.query';
import { NEW_MESSAGE_SUB } from 'services/graphql/subscriptions/message.subscription';
import { IQueryWithPagination } from 'types/common';
import { IChatRoom, IMessage } from 'types/messages';
import { defaultNotification } from 'utils';
import { v4 as uuidv4 } from 'uuid';

import { useManyDataQuery } from './useFastGqlQuery';

interface ISendMessageParams {
  message: string;
  chatRoomId: string;
}

export const useMutationSendMessage = () => {
  const currentUserData = useCurrentLoggedInUser(); // test nha
  const [sendMessage] = useMutation(SEND_MESSAGE, {
    update(cache, mutationResult) {
      const newMessage = mutationResult.data?.sendMessage;
      const existingMessagesData = cache.readQuery<{ getMessages: IQueryWithPagination<IMessage[]> }>({
        query: GET_MESSAGES,
        variables: { chatRoom: newMessage?.chatRoom?.id, pagination: { page: 1, take: 20 } },
      })?.getMessages || { data: [], meta: {} };

      const newMessagesListByDataKey = newMessage
        ? [newMessage, ...(existingMessagesData?.data || [])]
        : existingMessagesData.data;

      cache.writeQuery({
        query: GET_MESSAGES,
        variables: { chatRoom: newMessage?.chatRoom?.id, pagination: { page: 1, take: 20 } },
        data: {
          getMessages: {
            ...existingMessagesData,
            // data: [{ ...newMessagesListByDataKey[1], ...newMessagesListByDataKey[0] }, ...existingMessagesData.data],
            data: newMessagesListByDataKey,
          },
        },
      });
    },
  });

  const sendMessageMutation = async ({ chatRoomId: messageChatRoomId, message }: ISendMessageParams) => {
    const newMessageTempId = uuidv4();
    const newMessageEntity = {
      message,
      chatRoom: messageChatRoomId,
    };
    // setTempMessages((c) => ({ ...c, [newMessageTempId]: newMessageEntity }));
    try {
      await sendMessage({
        variables: {
          ...newMessageEntity,
        },
        optimisticResponse: {
          sendMessage: {
            __typename: 'Message',
            ...newMessageEntity,
            from: {
              __typename: 'User',
              id: currentUserData.id,
              firstName: currentUserData.firstName,
              lastName: currentUserData.lastName,
            },
            id: `${newMessagePlaceholderId}-${newMessageTempId}`,
            chatRoom: {
              __typename: 'ChatRoom',
              id: messageChatRoomId,
            },
            status: 'UNREAD',
            createdAt: moment().toLocaleString(),
          },
        },
      });
      // setTempMessages((c) => {
      //   const current = { ...c };
      //   current[newMessageTempId].sent = true;
      //   return current;
      // });
      // setAllowScrollToBottom(true);
    } catch (err) {
      defaultNotification({ failure: ERRORS.DEFAULT_MUTATION_ERROR });
    }
  };
  return sendMessageMutation;
};

export const useChatRoomData = ({ transactionId }: Partial<{ transactionId: string }> = {}) => {
  const userId = useCurrentLoggedInUser().id;
  const {
    content: chatRoomData,
    refetch: refetchChatRoomData,
    loading: chatRoomLoading,
    subscribeToMore,
  } = useManyDataQuery<IChatRoom>(GET_CHATROOM, {
    variables: { transaction: transactionId || undefined },
  });

  const subscribeToMessagesForUpdateChatRoom = () =>
    subscribeToMore<{ subMessages: IMessage[] }>({
      document: NEW_MESSAGE_SUB,
      variables: { id: userId },
      updateQuery: (prev, { subscriptionData }) => {
        const newMessageResponse = { ...(subscriptionData.data.subMessages?.[0] || {}) };
        const chatRoomConversationFromCache = prev.getChatRoomConversations;
        const chatRoomsDataFromCache: IChatRoom[] = chatRoomConversationFromCache?.data
          ? [...chatRoomConversationFromCache?.data]
          : [];
        const chatRoomIndexForNewMessage = chatRoomsDataFromCache.findIndex(
          (chatRoom) => chatRoom.id === newMessageResponse.chatRoom?.id,
        );
        if (chatRoomIndexForNewMessage > -1) {
          const newestChatRoomWithMessage = chatRoomsDataFromCache.splice(chatRoomIndexForNewMessage, 1)[0];
          const isNewMessageAlreadyInCache = newestChatRoomWithMessage.lastMessage?.id === newMessageResponse.id;
          return isNewMessageAlreadyInCache
            ? prev
            : {
                ...prev,
                getChatRoomConversations: {
                  ...chatRoomConversationFromCache,
                  data: [
                    {
                      ...newestChatRoomWithMessage,
                      lastMessage: newMessageResponse,
                      unreadMessagesCount:
                        newMessageResponse.from?.id === userId
                          ? newestChatRoomWithMessage.unreadMessagesCount
                          : (newestChatRoomWithMessage.unreadMessagesCount || 0) + 1,
                    },
                    ...chatRoomsDataFromCache,
                  ],
                },
              };
        }
        // refetchChatRoomData();
        return prev;
      },
    });

  const totalUnreadMessages = useMemo(
    () =>
      chatRoomData?.reduce((acc, chatRoom) => {
        const chatRoomUnread = checkUserIsInChatRoom(userId, chatRoom) ? chatRoom.unreadMessagesCount || 0 : 0;
        return acc + chatRoomUnread;
      }, 0),
    [chatRoomData],
  );

  return {
    chatRoomData,
    refetchChatRoomData,
    chatRoomLoading,
    totalUnreadMessages,
    subscribeToMessagesForUpdateChatRoom,
  };
};
